import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useCrmI18n } from "../../i18n/CrmI18nContext";
import axios from "axios";
import {
  FileCheck, Search, BarChart3, TrendingUp,
  Users, Award, Settings, LogOut, Sun, Star, Trash2,
  Menu, X, Building2, Eye, EyeOff, Target, Check, ScrollText, Database, ClipboardCheck, Activity, Scan, Brain, Download, ShieldCheck, Clock, Calendar, ChevronDown, ChevronRight, Briefcase, CalendarDays, Repeat, IdCard
} from "lucide-react";

import { API } from "../../utils/constants";
import { MobileStempelChip } from "./MobileStempelChip";

const SIDEBAR_KEYS = {
  "/tagesziel": "tagesziel",
  "/erfassung": "neukunde",
  "/google-bewertung": "google_bewertung",
  "/customers": "kunden_vertraege",
  "/hauttypenanalyse": "hauttypenanalyse",
  "/statistics": "statistiken",
  "/jahresstatistik": "jahresstatistik",
  "/intelligence": "intelligence",
  "/employees": "mitarbeiter",
  "/provisionsregeln": "provisionsregeln",
  "/papierkorb": "papierkorb",
  "/audit-log": "protokoll",
  "/soft-launch": "soft_launch",
  "/settings": "einstellungen",
};

// Frontend-only default-true Fallback fuer Keys, die im Backend
// DEFAULT_VISIBILITY (noch) NICHT existieren. Verhindert, dass das
// Hinzufuegen eines Eye-Mappings zu einer Funktionsregression fuehrt
// (Admin wuerde Item nicht mehr sehen, weil adminVisibility[key]===undefined).
// Superadmin kann durch aktiven Toggle weiterhin auf false setzen.
const ADMIN_DEFAULT_TRUE_KEYS = new Set([
  "protokoll",
  "soft_launch",
]);

// Admin-Modus B (steuert Mitarbeiter-Sichtbarkeit) darf diese Keys NICHT
// fuer Mitarbeiter freigeben. Superadmin-Modus A (steuert Admin-Sichtbarkeit)
// bleibt unbeschraenkt.
const EMPLOYEE_TOGGLE_BLOCKED_KEYS = new Set([
  "papierkorb",
  "einstellungen",
  "mitarbeiter",
  "jahresstatistik",
  "protokoll",
  "soft_launch",
]);

// WORKFORCE-SHELL-NAVIGATION-UNIFICATION-V1
// Sidebar-Link "Zeiterfassung" zeigt aktiv, wenn Operator auf einer
// der 9 Workforce-Routen ist (Monatsplan, Stempeluhr, Schichtplan, ...).
const WORKFORCE_PATHS = new Set([
  "/monatsplan", "/mein-bereich", "/zeiterfassung", "/schichtplan", "/abwesenheiten",
  "/urlaubskonto", "/soll-ist", "/ueberstunden", "/schichttausch", "/abdeckung",
  "/lohnabschluss",
]);
// EMPLOYEE-MENU-UNIFICATION-V1
const EMPLOYEE_PATHS = new Set(["/employees", "/mitarbeiterakte"]);
// ADMIN-MENU-CONSOLIDATION-AND-AUDIT-LAYOUT-FIX-V1
const DATA_INTEGRITY_PATHS = new Set([
  "/ml-coverage", "/audit-log", "/audit", "/soft-launch",
]);

export const Sidebar = () => {
  const { user, logout, isAdmin, activeStudio, authReady } = useAuth();
  const { t } = useCrmI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [adminVisibility, setAdminVisibility] = useState(null);
  const [employeeVisibility, setEmployeeVisibility] = useState(null);
  const [studioLogo, setStudioLogo] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  // Expand-State pro Sidebar-Gruppe (Persist via sessionStorage). Wird
  // automatisch geoeffnet, wenn aktive Route in der Gruppe liegt.
  const [groupOpen, setGroupOpen] = useState(() => {
    try {
      const raw = sessionStorage.getItem("sidebar_group_open");
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const toggleGroup = useCallback((gid) => {
    setGroupOpen((prev) => {
      const next = { ...prev, [gid]: !prev[gid] };
      try { sessionStorage.setItem("sidebar_group_open", JSON.stringify(next)); } catch (_e) {}
      return next;
    });
  }, []);

  const isSuperAdmin = user?.role === "super_admin";
  const isRegularAdmin = user?.role === "admin";
  const isEmployee = user?.role === "employee";
  // S-TABLET-RBAC-CUSTOMERS-CONTRACTS-V1: Tablet-Modus.
  // Sidebar zeigt im Tablet-Modus EXKLUSIV 2 Eintraege: Hauttypanalyse +
  // Kunden & Vertraege. Alle Visibility-Toggles/Eyes werden ignoriert.
  // Direktklick auf andere Pfade wird zusaetzlich durch ProtectedRoute
  // abgefangen — defensive Doppelung.
  const isTablet = user?.role === "tablet";

  // PWA Install Prompt
  const deferredPromptRef = useRef(null);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setCanInstall(true);
    };
    const onInstalled = () => {
      deferredPromptRef.current = null;
      setCanInstall(false);
    };
    // Don't show if already in standalone mode (already installed)
    if (window.matchMedia("(display-mode: standalone)").matches) return;
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPromptRef.current) return;
    deferredPromptRef.current.prompt();
    const { outcome } = await deferredPromptRef.current.userChoice;
    if (outcome === "accepted") {
      deferredPromptRef.current = null;
      setCanInstall(false);
    }
  };

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.classList.add('sidebar-mobile-open');
    } else {
      document.body.classList.remove('sidebar-mobile-open');
    }
    return () => { document.body.classList.remove('sidebar-mobile-open'); };
  }, [mobileOpen]);

  // S-TABLET-RBAC-CUSTOMERS-LIVE-FIX-V1: Body-Class fuer Tablet-Rolle.
  // CSS-Regeln unter `body.role-tablet` zwingen die Sidebar in den
  // Off-Canvas-Hamburger-Modus, unabhaengig vom Viewport. Damit nutzt
  // der Tablet-Operator die volle Inhaltsbreite und schliesst die
  // Sidebar automatisch nach Auswahl (Z.138 handhabt das via location).
  useEffect(() => {
    if (isTablet) {
      document.body.classList.add('role-tablet');
    } else {
      document.body.classList.remove('role-tablet');
    }
    return () => { document.body.classList.remove('role-tablet'); };
  }, [isTablet]);

  // Load visibility (only when auth is fully ready)
  useEffect(() => {
    if (!user || !authReady) return;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/sidebar-visibility`);
        setAdminVisibility(res.data.admin_visibility || {});
        setEmployeeVisibility(res.data.employee_visibility || {});
      } catch {
        const fallback = {
          tagesziel: true, neukunde: true, google_bewertung: true,
          kunden_vertraege: true, statistiken: true, jahresstatistik: true,
          intelligence: true,
          mitarbeiter: true, provisionsregeln: true, papierkorb: false, einstellungen: false,
        };
        setAdminVisibility(fallback);
        setEmployeeVisibility(fallback);
      }
    };
    load();
  }, [user, authReady]);

  // Use studio logo from AuthContext (already fetched during initialization)
  useEffect(() => {
    if (!user || !authReady) return;
    setStudioLogo(activeStudio?.logo_url || null);
  }, [user, authReady, activeStudio]);

  // Toggle — Super Admin toggles admin_visibility, Admin toggles employee_visibility
  const toggleVisibility = useCallback(async (key) => {
    const isSA = user?.role === "super_admin";
    const currentVis = isSA ? adminVisibility : employeeVisibility;
    const newVisible = !(currentVis?.[key] === true);

    // Optimistic update
    if (isSA) {
      setAdminVisibility(prev => ({ ...prev, [key]: newVisible }));
      // Cascade: hiding from admin also hides from employee
      if (!newVisible) {
        setEmployeeVisibility(prev => ({ ...prev, [key]: false }));
      }
    } else {
      setEmployeeVisibility(prev => ({ ...prev, [key]: newVisible }));
    }

    setSaveStatus("saving");
    try {
      const res = await axios.patch(`${API}/sidebar-visibility`, { key, visible: newVisible });
      if (res.data?.admin_visibility) setAdminVisibility(res.data.admin_visibility);
      if (res.data?.employee_visibility) setEmployeeVisibility(res.data.employee_visibility);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(null), 1200);
    } catch {
      // Revert
      if (isSA) {
        setAdminVisibility(prev => ({ ...prev, [key]: !newVisible }));
      } else {
        setEmployeeVisibility(prev => ({ ...prev, [key]: !newVisible }));
      }
      setSaveStatus("error");
      setTimeout(() => setSaveStatus(null), 2000);
    }
  }, [user, adminVisibility, employeeVisibility]);

  const navItems = [
    { path: "/tagesziel", icon: Target, label: t("sidebar.nav.tagesziel"), show: true },
    // S-NAV-CUSTOMERS-FLAT-V1 (2026-05-31, Architekten-Direktive):
    // Group-Variante (Kundenverwaltung + Neukunde als Children) rueckgebaut.
    // Klick auf "Kunden & Verträge" oeffnet direkt /customers (Kundenliste).
    // Der Einstieg fuer Neukunden-Erfassung erfolgt jetzt als Toolbar-Button
    // "+ Neukunde" auf der Kundenverwaltungs-Seite selbst (neben Export).
    // Route `/erfassung` bleibt unangetastet als Ziel des neuen Buttons.
    { path: "/google-bewertung", icon: Star, label: t("sidebar.nav.google_bewertung"), show: true },
    { path: "/customers", icon: Search, label: t("sidebar.nav.kunden_vertraege"), show: true },
    { path: "/hauttypenanalyse", icon: Scan, label: t("sidebar.nav.hauttypenanalyse"), show: true },
    { path: "/statistics", icon: BarChart3, label: t("sidebar.nav.statistiken"), show: true },
    // S-NAV-PROVISION-ABOVE-TIMETRACKING-V1 (2026-05-31, Architekten-Direktive):
    // Provisionsregeln steht jetzt VOR Zeiterfassung im Team-/Mitarbeiter-
    // Bereich der Hauptnavigation. Reine Reihenfolge-Aenderung. Routen,
    // Komponenten, Berechtigungen, Sichtbarkeiten, API und Datenmodell
    // bleiben unangetastet.
    { path: "/provisionsregeln", icon: Award, label: t("sidebar.nav.provisionsregeln"), show: true },
    // WORKFORCE-SELF-SERVICE-RBAC-001 (2026-05-30):
    // Admin -> /monatsplan (AdminRoute). Mitarbeiter -> /mein-bereich
    // (Self-Service-Hub), damit der Menupunkt nicht in einen 403-Redirect
    // fuehrt.
    // ARR-STUDIO-SCHEDULE-TODAY (2026-09-05): role=studio -> /schichtplan
    // (read-only Tagesbesetzung "Heute im Studio"), nicht der Employee-Hub.
    { path: (isSuperAdmin || isRegularAdmin) ? "/monatsplan"
        : user?.role === "studio" ? "/schichtplan" : "/mein-bereich",
      icon: Briefcase, label: isEmployee ? "Mein Bereich" : "Mitarbeiter & Planung", show: true },
    // S-STATISTICS-YEARLY-TRUTH-ALIGNMENT-V1 (2026-05-31):
    // Jahresstatistik wurde aus der Hauptnavigation entfernt und ist nur noch
    // ueber `/statistics` als Tab erreichbar. Die Route `/jahresstatistik`
    // bleibt erhalten (Bookmarks, Direkt-Links).
    // EMPLOYEE-MENU-UNIFICATION-V1: "Mitarbeiterakte" entfernt als
    // eigener Hauptpunkt — jetzt als Tab innerhalb "Mitarbeiter".
    { path: "/employees", icon: Users, label: t("sidebar.nav.mitarbeiter"), show: isSuperAdmin || isRegularAdmin },
    { path: "/intelligence", icon: Brain, label: t("sidebar.nav.intelligence"), show: isRegularAdmin || isSuperAdmin },
    { path: "/papierkorb", icon: Trash2, label: t("sidebar.nav.papierkorb"), show: isSuperAdmin || isRegularAdmin },
    // ADMIN-MENU-CONSOLIDATION-AND-AUDIT-LAYOUT-FIX-V1: 4 Adminseiten
    // (Datenwahrheit-Monitor + Protokoll + Audit-Dashboard + Soft-Launch)
    // unter EINEM Sidebar-Hauptpunkt mit Top-Tabs.
    { path: "/ml-coverage", icon: ShieldCheck, label: t("sidebar.nav.ml_coverage"), show: isSuperAdmin || isRegularAdmin },
    { path: "/settings", icon: Settings, label: t("sidebar.nav.einstellungen"), show: isSuperAdmin || isRegularAdmin },
    { path: "/super-admin", icon: Building2, label: t("sidebar.nav.studios"), show: isSuperAdmin },
    { path: "/daten-import", icon: Database, label: t("sidebar.nav.daten_import"), show: isSuperAdmin },
    { path: "/daten-pruefung", icon: ClipboardCheck, label: t("sidebar.nav.datenpruefung"), show: isSuperAdmin },
    // S-SYSTEM-STATUS-DIAGNOSTICS-UI-V1 (2026-06-10): Read-Only-Diagnostik-UI
    // (Messdaten-Integritaet + DB-Indizes) statt curl-Abrufe.
    { path: "/system-status", icon: Activity, label: "System-Status", show: isSuperAdmin },
  ];

  const handleLogout = () => { logout(); navigate("/login"); };

  const handleLogoClick = () => {
    window.dispatchEvent(new CustomEvent('suntime-logo-click'));
    navigate("/dashboard");
    setMobileOpen(false);
  };

  // Filter items based on role hierarchy
  // S-NAV-NEUKUNDE-UNDER-CUSTOMERS-V1 (2026-05-31):
  // Group-Items koennen jetzt einen optionalen `visibilityKey` tragen. Wenn
  // dieser Key fuer die aktuelle Rolle nicht sichtbar ist, wird die ganze
  // Gruppe gefiltert. Falls die Gruppe sichtbar ist, werden ihre Children
  // im Render-Loop einzeln gegen ihren eigenen `visibilityKey` gefiltert.
  const isKeyVisibleForRole = (key) => {
    if (!key) return true;
    if (isSuperAdmin) return true;
    if (isRegularAdmin) {
      if (ADMIN_DEFAULT_TRUE_KEYS.has(key) && adminVisibility?.[key] === undefined) {
        return true;
      }
      return adminVisibility?.[key] === true;
    }
    return employeeVisibility?.[key] === true;
  };
  const getFilteredItems = () => {
    // S-TABLET-RBAC-CUSTOMERS-CONTRACTS-V1: Tablet-Modus uebersteuert ALLE
    // Visibility-Toggles. Nur Hauttypanalyse + Kunden & Vertraege sind sichtbar.
    if (isTablet) {
      const TABLET_PATHS = new Set(["/hauttypenanalyse", "/customers"]);
      return navItems.filter(item =>
        item.type !== "group" && item.path && TABLET_PATHS.has(item.path)
      );
    }
    // S-EMPLOYEE-RBAC-FIXED-MENU-V1: Mitarbeiter-Rolle sieht IMMER genau
    // diese Punkte — unabhaengig von den Sichtbarkeits-Schaltern:
    // "Tagesziel" + "Mein Bereich" (Self-Service-Hub, Briefcase-Item).
    // Die Studio-Rolle folgt bewusst weiter den normalen Eye-Toggles,
    // damit neue Features dort automatisch sichtbar werden.
    if (isEmployee) {
      const EMPLOYEE_WHITELIST_PATHS = new Set(["/tagesziel", "/mein-bereich"]);
      return navItems.filter(item =>
        item.type !== "group" && item.path && EMPLOYEE_WHITELIST_PATHS.has(item.path)
      );
    }
    return navItems.filter((item) => {
      if (!item.show) return false;
      if (item.type === "group") {
        // Group ohne `visibilityKey` zeigt sich immer (z.B. Workforce-Group).
        // Group mit `visibilityKey` folgt der gleichen Sichtbarkeitsregel wie
        // normale Items.
        if (item.visibilityKey) return isKeyVisibleForRole(item.visibilityKey);
        return true;
      }
      const key = SIDEBAR_KEYS[item.path];
      if (!key) return true; // Items without key (e.g. Studios) use their own show logic
      return isKeyVisibleForRole(key);
    });
  };

  // Get the eye state for current role
  const getEyeState = (key) => {
    if (isSuperAdmin) return adminVisibility?.[key] === true;
    if (isRegularAdmin) return employeeVisibility?.[key] === true;
    return true;
  };

  // Eye tooltip based on role
  const getEyeTooltip = (key) => {
    const visible = getEyeState(key);
    if (isSuperAdmin) {
      return visible ? t("common.visibility.visible_admin") : t("common.visibility.hidden_admin");
    }
    return visible ? t("common.visibility.visible_employee") : t("common.visibility.hidden_employee");
  };

  // Can this admin toggle this key? (Admin can only toggle items they can see)
  const canToggle = (key) => {
    if (isSuperAdmin) return true;
    if (isRegularAdmin) {
      // Modus B: Admin steuert Mitarbeiter-Sichtbarkeit. Bestimmte
      // admin-only Bereiche sind fuer Mitarbeiter NICHT freigebbar -
      // dort wird kein Auge angezeigt.
      if (EMPLOYEE_TOGGLE_BLOCKED_KEYS.has(key)) return false;
      return adminVisibility?.[key] === true;
    }
    return false;
  };

  const SidebarContent = () => (
    <>
      <div className="sidebar-logo" onClick={handleLogoClick} style={{ cursor: "pointer" }} title={t("sidebar.home_title")}>
        {studioLogo ? (
          <img src={studioLogo} alt="Studio Logo" style={{ width: 28, height: 28, objectFit: "contain", borderRadius: 4 }} />
        ) : (
          <Sun size={24} />
        )}
        <span className="sidebar-logo-text">{activeStudio?.name || t("common.brand")}</span>
      </div>

      <nav style={{ flex: 1 }}>
        {/* Save status */}
        {isAdmin && saveStatus && (
          <div style={{
            padding: "4px 12px", fontSize: 11,
            color: saveStatus === "error" ? "var(--sun-error)" : saveStatus === "saved" ? "var(--sun-success)" : "var(--sun-text-muted)",
            display: "flex", alignItems: "center", gap: 4, justifyContent: "center",
          }}>
            {saveStatus === "saving" && t("common.save.saving")}
            {saveStatus === "saved" && <><Check size={12} /> {t("common.save.saved")}</>}
            {saveStatus === "error" && t("common.save.error")}
          </div>
        )}

        {/* Role indicator for admins */}
        {isAdmin && (
          <div style={{
            padding: "4px 12px", fontSize: 10, fontWeight: 600,
            color: "var(--sun-text-muted)", textAlign: "center", letterSpacing: 0.5,
            borderBottom: "1px solid var(--sun-border)", marginBottom: 4, paddingBottom: 6,
          }}>
            {isSuperAdmin ? t("sidebar.role_indicator.super_admin") : t("sidebar.role_indicator.admin")}
          </div>
        )}

        {getFilteredItems().map((item) => {
          // ─── Gruppe (z.B. "Zeiterfassung" oder "Kunden & Verträge") ──
          if (item.type === "group") {
            // S-NAV-NEUKUNDE-UNDER-CUSTOMERS-V1: Children koennen einen
            // eigenen `visibilityKey` tragen. Sie werden hier vor Render
            // gegen die Rollen-Sichtbarkeit gefiltert.
            const visibleChildren = item.children.filter((child) => {
              if (!child.visibilityKey) return true;
              // Gleiche Regel wie fuer Hauptitems.
              if (isSuperAdmin) return true;
              if (isRegularAdmin) {
                if (ADMIN_DEFAULT_TRUE_KEYS.has(child.visibilityKey) && adminVisibility?.[child.visibilityKey] === undefined) {
                  return true;
                }
                return adminVisibility?.[child.visibilityKey] === true;
              }
              return employeeVisibility?.[child.visibilityKey] === true;
            });
            if (visibleChildren.length === 0) return null;
            const childPaths = visibleChildren.map((c) => c.path);
            const isActiveGroup = childPaths.includes(location.pathname);
            // Wenn aktive Route in Gruppe liegt UND noch nicht ge-toggelt:
            // Auto-Expand. Wenn vom User explizit zugeklappt, respektiere das.
            const explicitlySet = Object.prototype.hasOwnProperty.call(groupOpen, item.id);
            const open = explicitlySet ? groupOpen[item.id] : isActiveGroup;
            return (
              <div key={item.id} data-testid={`nav-group-${item.id}`}>
                <button
                  type="button"
                  onClick={() => toggleGroup(item.id)}
                  className={`sidebar-link sidebar-group-toggle ${isActiveGroup ? "is-active-parent" : ""}`}
                  data-testid={`nav-group-toggle-${item.id}`}
                  style={{
                    width: "100%", border: "none",
                    textAlign: "left", cursor: "pointer", minHeight: 44,
                    display: "flex", alignItems: "center", gap: 10,
                  }}
                >
                  <item.icon size={18} />
                  <span className="sidebar-link-text" style={{ flex: 1 }}>{item.label}</span>
                  {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                {open && (
                  <div data-testid={`nav-group-children-${item.id}`}
                       style={{ paddingLeft: 16 }}>
                    {visibleChildren.map((child) => {
                      // S-NAV-NEUKUNDE-UNDER-CUSTOMERS-V1: Eye-Toggle pro
                      // Child analog zum normalen Item. Nur wenn der Admin/
                      // Superadmin den Key togglen darf (canToggle).
                      const childKey = child.visibilityKey;
                      const childShowEye = isAdmin && childKey && canToggle(childKey);
                      const childEyeActive = getEyeState(childKey);
                      return (
                        <div key={child.path} style={{ display: "flex", alignItems: "center", position: "relative" }}>
                          <Link
                            to={child.path}
                            className={`sidebar-link ${location.pathname === child.path ? "active" : ""}`}
                            data-testid={`nav-${child.path.replace("/", "")}`}
                            onClick={() => setMobileOpen(false)}
                            style={{ flex: 1, fontSize: 13, minHeight: 40 }}
                          >
                            <child.icon size={16} />
                            <span className="sidebar-link-text">{child.label}</span>
                          </Link>
                          {childShowEye && (
                            <button
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleVisibility(childKey); }}
                              data-testid={`sidebar-eye-${childKey}`}
                              title={getEyeTooltip(childKey)}
                              style={{
                                background: "none", border: "none", cursor: "pointer",
                                padding: "4px 8px", display: "flex", alignItems: "center",
                                justifyContent: "center", flexShrink: 0,
                              }}
                            >
                              {childEyeActive ? (
                                <Eye size={14} style={{ color: "#4a9fd5", strokeWidth: 2.2 }} />
                              ) : (
                                <EyeOff size={14} style={{ color: "#999", strokeWidth: 1.5, opacity: 0.4 }} />
                              )}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          // ─── Normales Item ─────────────────────────────────────────
          const sidebarKey = SIDEBAR_KEYS[item.path];
          const showEye = isAdmin && sidebarKey && canToggle(sidebarKey);
          const eyeActive = getEyeState(sidebarKey);
          // WORKFORCE-SHELL-NAVIGATION-UNIFICATION-V1: Zeiterfassung-
          // Item aktiv auch auf jeder Workforce-Sub-Route.
          const isWorkforceLink = item.label === "Zeiterfassung";
          const isEmployeeLink = item.path === "/employees";
          const isDataIntegrityLink = item.path === "/ml-coverage";
          const isItemActive = location.pathname === item.path
            || (isWorkforceLink && WORKFORCE_PATHS.has(location.pathname))
            || (isEmployeeLink && EMPLOYEE_PATHS.has(location.pathname))
            || (isDataIntegrityLink && DATA_INTEGRITY_PATHS.has(location.pathname));

          return (
            <div key={item.path} style={{ display: "flex", alignItems: "center", position: "relative" }}>
              <Link
                to={item.path}
                className={`sidebar-link ${isItemActive ? "active" : ""}`}
                data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
                onClick={() => setMobileOpen(false)}
                style={{ flex: 1 }}
              >
                <item.icon size={18} />
                <span className="sidebar-link-text">{item.label}</span>
              </Link>
              {showEye && (
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleVisibility(sidebarKey); }}
                  data-testid={`sidebar-eye-${sidebarKey}`}
                  title={getEyeTooltip(sidebarKey)}
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    padding: "4px 8px", display: "flex", alignItems: "center",
                    justifyContent: "center", flexShrink: 0,
                  }}
                >
                  {eyeActive ? (
                    <Eye size={14} style={{ color: "#4a9fd5", strokeWidth: 2.2 }} />
                  ) : (
                    <EyeOff size={14} style={{ color: "#999", strokeWidth: 1.5, opacity: 0.4 }} />
                  )}
                </button>
              )}
            </div>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        {/* PWA Install Button — only when beforeinstallprompt is available */}
        {canInstall && (
          <button
            onClick={handleInstallClick}
            data-testid="pwa-install-btn"
            style={{
              display: "flex", alignItems: "center", gap: 8,
              width: "100%", padding: "10px 12px", marginBottom: 8,
              background: "linear-gradient(135deg, var(--sun-gold) 0%, var(--sun-gold-dark) 100%)",
              border: "none", borderRadius: 8, cursor: "pointer",
              color: "#1a1a2e", fontSize: 13, fontWeight: 600,
              transition: "opacity 0.2s",
            }}
          >
            <Download size={16} />
            {t("sidebar.install_app")}
          </button>
        )}
        {isSuperAdmin && activeStudio && (
          <div
            style={{
              padding: "10px 12px", marginBottom: 12,
              background: "linear-gradient(135deg, var(--sun-gold) 0%, var(--sun-gold-dark) 100%)",
              borderRadius: 8, color: "#1a1a2e"
            }}
            data-testid="active-studio-indicator"
          >
            <div style={{ fontSize: 10, opacity: 0.8, marginBottom: 4, fontWeight: 600 }}>{t("sidebar.active_studio")}</div>
            <div style={{ fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
              <Building2 size={14} /> {activeStudio.name}
            </div>
          </div>
        )}
        {/* UX-2 §27: Seltene Admin-Systembereiche im Profil-/Footer-Bereich
            discoverable (nur admin/super_admin — Routen bleiben AdminRoute-
            geschuetzt, keine neuen Rechte, Tablet/Employee unveraendert). */}
        {(isRegularAdmin || isSuperAdmin) && !getFilteredItems().some((i) => i.path === "/settings" || i.path === "/intelligence") && (
          <div data-testid="sidebar-system-links" style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 8 }}>
            <button
              onClick={() => { navigate("/settings"); setMobileOpen(false); }}
              className="sidebar-link"
              style={{ width: "100%", fontSize: 12, minHeight: 36 }}
              data-testid="sidebar-system-settings"
            >
              <Settings size={15} />
              <span className="sidebar-link-text">{t("sidebar.nav.einstellungen")}</span>
            </button>
            <button
              onClick={() => { navigate("/intelligence"); setMobileOpen(false); }}
              className="sidebar-link"
              style={{ width: "100%", fontSize: 12, minHeight: 36 }}
              data-testid="sidebar-system-intelligence"
            >
              <Brain size={15} />
              <span className="sidebar-link-text">{t("sidebar.nav.intelligence")}</span>
            </button>
          </div>
        )}
        <div className="sidebar-user">
          <div className="sidebar-user-name" data-testid="user-name">{user?.name}</div>
          <div className="sidebar-user-role" data-testid="user-role">
            {user?.role === "admin" ? t("common.roles.admin") : user?.role === "super_admin" ? t("common.roles.super_admin") : user?.role === "tablet" ? "Tablet" : t("common.roles.employee")}
          </div>
        </div>
        <button onClick={handleLogout} className="sidebar-link sidebar-logout" data-testid="logout-button">
          <LogOut size={18} />
          <span className="sidebar-link-text">{t("sidebar.logout")}</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      <div className="mobile-header" data-testid="mobile-header">
        <button className="hamburger-btn" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn" aria-label={t("sidebar.menu_open")}>
          <Menu size={24} />
        </button>
        <div className="mobile-header-logo" onClick={handleLogoClick} style={{ cursor: "pointer" }}>
          {studioLogo ? (
            <img src={studioLogo} alt="Logo" style={{ width: 22, height: 22, objectFit: "contain", borderRadius: 3 }} />
          ) : (
            <Sun size={20} />
          )}
          <span>{activeStudio?.name || t("common.brand")}</span>
        </div>
        {isEmployee && <MobileStempelChip />}
        <div className="mobile-header-user">{user?.name?.charAt(0)}</div>
      </div>

      {mobileOpen && <div className="mobile-overlay" onClick={() => setMobileOpen(false)} data-testid="mobile-overlay" />}

      <div className={`sidebar ${mobileOpen ? "mobile-open" : ""}`} data-testid="sidebar">
        <button className="mobile-close-btn" onClick={() => setMobileOpen(false)} aria-label={t("sidebar.menu_close")}>
          <X size={24} />
        </button>
        <SidebarContent />
      </div>
    </>
  );
};

export default Sidebar;
